import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ME_QUERY_KEY, useAuthStore } from "./auth-store.js";
import { refreshSession, type SessionClaims } from "./session-api.js";

/** Used when the BFF does not declare its cookie lifetime. */
const FALLBACK_IDLE_TTL_SECONDS = 1800;

/** Preferred minimum interval; shorter TTLs relax it to stay below expiry. */
const MIN_INTERVAL_MS = 30_000;
/** Never tick slower than this, however long the declared lifetime is. */
const MAX_INTERVAL_MS = 10 * 60_000;
/** Spread ticks so a deploy does not make every client renew in the same second. */
const JITTER = 0.15;

/**
 * What counts as someone being here.
 *
 * `pointermove` and `wheel` are load-bearing, not padding. Moving the cursor is
 * what drives the Yjs awareness broadcast, and panning by wheel is how a board
 * gets read — between them they are the most continuous traffic a workshop puts
 * on the socket, and both are completely silent on `pointerdown`/`keydown`.
 * Watching only clicks and keys would let someone read and pan a board for half
 * an hour while the session counted them as idle, which is the reported defect
 * wearing a different hat.
 *
 * Each handler does nothing but set a flag, so `pointermove`'s firing rate buys
 * one boolean assignment and needs no throttle.
 */
const PRESENCE_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
] as const;

/**
 * Keeps a PRESENT person signed in.
 *
 * The session cookie is only re-issued by a request that reaches the BFF, so a
 * stretch with no API calls lets it lapse. That is not hypothetical: in a
 * Hoshin workshop nearly all work — typing on a sticky note, dragging it,
 * grouping — travels over the Yjs socket, and REST stays silent while the room
 * is busy. The session then ends mid-session and the facilitator is signed out
 * in front of the room (PB-4900).
 *
 * **Presence is the whole design, not a refinement.** A timer that renewed
 * whenever a tab was open would delete the idle timeout outright — a laptop
 * left on a desk would stay signed in for as long as the browser did. So a tick
 * renews only when BOTH hold: the tab is visible, and a real interaction
 * happened since the last tick. An abandoned tab renews nothing and expires
 * exactly as it should today.
 *
 * **This is a proxy for Yjs activity, not Yjs activity.** What it watches is
 * input in this tab; what a workshop actually produces is traffic on the
 * collaboration socket. The two correlate, and PRESENCE_EVENTS below is chosen
 * to make them correlate closely — but they are not the same signal, and no
 * arrangement of DOM listeners makes them the same signal. Making socket
 * activity genuinely count as session activity means the collaboration layer
 * telling Auth that a person is alive, which is a system-level decision and
 * does not exist today: the one channel between them runs the other way
 * (playbook-be to collab) and carries authorization, not lifetime. Until that
 * link exists this hook is the stopgap, and should be read as one.
 *
 * Ticking at HALF the declared lifetime leaves a full half-life to recover in
 * when one tick is missed (a suspended tab, a transient failure). The effective
 * idle window therefore stretches by up to one interval beyond the configured
 * TTL, which is a deliberate and bounded trade for not signing out a working
 * person.
 *
 * Failures are swallowed on purpose: `/me` owns the verdict on whether a
 * session is really gone, and a keep-alive must not race it into a second,
 * competing sign-in.
 *
 * A successful renew also reloads `/me` (PB-1589, ADR-0100). The refresh
 * re-derives the session's module list from Postgres, so an administrator's
 * module switch reaches this person on their next tick instead of only after
 * a reload. That is one extra GET per tick, and no polling of its own.
 *
 * Mount ONCE, high in the authenticated tree.
 */
export function useSessionKeepAlive(): void {
  const status = useAuthStore((s) => s.status);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (status !== "authenticated") return;

    const claims = queryClient.getQueryData<SessionClaims | null>(ME_QUERY_KEY);
    const ttlSeconds =
      claims?.sessionIdleTtlSeconds ?? FALLBACK_IDLE_TTL_SECONDS;
    const ttlMs = ttlSeconds * 1000;
    const half = Math.round(ttlMs / 2);
    const minimum = Math.min(MIN_INTERVAL_MS, half);
    const intervalMs = Math.min(
      MAX_INTERVAL_MS,
      ttlMs - 1,
      Math.max(
        minimum,
        Math.round(half * (1 + (Math.random() * 2 - 1) * JITTER)),
      ),
    );

    let interactedSinceLastTick = false;
    const noteInteraction = (): void => {
      interactedSinceLastTick = true;
    };

    // Capture phase: a canvas that stops propagation on its own surface would
    // otherwise hide exactly the activity this hook exists to notice. Passive,
    // so listening can never delay input handling.
    const options = { capture: true, passive: true } as const;
    for (const event of PRESENCE_EVENTS) {
      window.addEventListener(event, noteInteraction, options);
    }

    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (!interactedSinceLastTick) return;
      // Cleared before the request, so interaction DURING a slow renew still
      // counts towards the next tick rather than being swallowed by this one.
      interactedSinceLastTick = false;
      void refreshSession()
        .then(() => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }))
        .catch(() => {
          // Intentionally silent — see the note above.
        });
    }, intervalMs);

    return () => {
      clearInterval(timer);
      for (const event of PRESENCE_EVENTS) {
        window.removeEventListener(event, noteInteraction, options);
      }
    };
  }, [status, queryClient]);
}
